import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for intersection observer with performance optimizations
 * @param {object} options - Intersection observer options
 * @returns {object} - { ref, isIntersecting, hasIntersected }
 */
const useIntersectionObserver = (options = {}) => {
  const elementRef = useRef(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting;
          setIsIntersecting(isVisible);
          
          // Track if element has ever been visible
          if (isVisible && !hasIntersected) {
            setHasIntersected(true);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
        ...options
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [options, hasIntersected]);

  return { ref: elementRef, isIntersecting, hasIntersected };
};

export default useIntersectionObserver;
